<template>
    <div class="radio-wrapper">
        <label :class="['radio-container', `align-${computedAlignment}`]">
            <input type="radio" v-model="groupValue" :value="value" class="radio-input" ref="radioRef"
                :name="computedName">
            <span class="radio-custom" :class="{
                'small': computedSize === 'small',
                'medium': computedSize === 'medium',
                'big': computedSize === 'big',
                [color]: true
            }"></span>
            <span class="radio-label">
                <slot>{{ label }}</slot>
            </span>
        </label>
    </div>
</template>
<script setup>
import { ref, computed, inject } from 'vue'

const props = defineProps({
    label: {
        type: String,
        default: 'label'
    },
    modelValue: {
        type: [String, Number, Boolean, Object],
        default: ''
    },
    value: {
        type: [String, Number, Boolean, Object],
        default: ''
    },
    groupName: {
        type: String,
        default: 'radio-group'
    },
    alignment: {
        type: String,
        default: 'middle',
        validator: (value) => ['top', 'middle', 'bottom'].includes(value)
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) => ['small', 'medium', 'big'].includes(value)
    },
    color: {
        type: String,
        default: 'primary',
        validator: (value) => ['default', 'primary', 'secondary', 'success', 'danger', 'orange', 'warning', 'info', 'white', 'green', 'light-green', 'gray'].includes(value)
    }
    
})
const emit = defineEmits(['update:modelValue'])
const radioRef = ref(null)

// Try to inject values from parent ZSRadioGroup
const radioGroupModel = inject('radioGroupModel', null)
const radioGroupName = inject('radioGroupName', null)
const radioGroupSize = inject('radioGroupSize', null)
const radioGroupAlignment = inject('radioGroupAlignment', null)
const radioGroupColor = inject('radioGroupColor', null)

// Use injected values if available, otherwise use prop values
const computedName = computed(() => radioGroupName || props.groupName)
const computedSize = computed(() => radioGroupSize || props.size)
const computedAlignment = computed(() => radioGroupAlignment || props.alignment)
const computedColor = computed(() => radioGroupColor || props.color)

// If within a radio group, use the group's model, otherwise use its own
const groupValue = computed({
    get: () => {
        if (radioGroupModel) {
            return radioGroupModel.value
        }
        return props.modelValue
    },
    set: (value) => {
        if (radioGroupModel) {
            radioGroupModel.value = value
        } else {
            emit('update:modelValue', value)
        }
    }
})
</script>
<style >
:root {
    /* Colors */
    --color-radio-label: #333;
    --color-radio-default-background: #F9F9F9;
    --color-radio-default-border: #D0E0EE;
    --color-radio-focus-shadow: rgba(176, 229, 245, 0.3);

    /* Color variants */
    --color-orange: #EB7629;
    --color-primary: #4384BD;
    --color-secondary: #6c757d;
    --color-success: #27AE60;
    --color-danger: #E74C3C;
    --color-warning: #ffc107;
    --color-info: #F9F9F9;
    --color-white: #FFFFFF;
    --color-green: #27AE60;
    --color-light-green: #EAFAEB;
    --color-gray: #787878;

    /* RadioButton Base */
    --zs-radio-font-family: "Poppins", sans-serif;
    --zs-radio-padding: 8px;
    --zs-radio-container-padding-left: 30px;
    --zs-radio-container-user-select: none;

    /* RadioButton Custom */
    --zs-radio-default-size: 18px;
    --zs-radio-default-background: var(--color-radio-default-background);
    --zs-radio-default-border: 1px solid var(--color-radio-default-border);
    --zs-radio-default-border-radius: 50%;
    --zs-radio-transition: all 0.2s ease;

    /* RadioButton sizes */
    --zs-radio-small-size: 16px;
    --zs-radio-medium-size: 22px;
    --zs-radio-big-size: 28px;

    /* RadioButton inner dot */
    --zs-radio-dot-small-size: 8px;
    --zs-radio-dot-small-position: 3px;
    --zs-radio-dot-medium-size: 10px;
    --zs-radio-dot-medium-position: 5px;
    --zs-radio-dot-big-size: 18px;
    --zs-radio-dot-big-position: 4px;

    /* RadioButton Label */
    --zs-radio-label-color: var(--color-radio-label);
    --zs-radio-label-font-size: 12px;
    --zs-radio-label-margin-left: 10px;
    --zs-radio-label-font-family: "Poppins";

    /* RadioButton Focus */
    --zs-radio-focus-shadow: 0 0 0 2px var(--color-radio-focus-shadow);
}

.radio-wrapper {
    font-family: var(--zs-radio-font-family);
    padding: var(--zs-radio-padding);
}

.radio-container {
    display: flex;
    cursor: pointer;
    position: relative;
    padding-left: var(--zs-radio-container-padding-left);
    user-select: var(--zs-radio-container-user-select);
    width: 100%;

    &.align-top {
        align-items: flex-start;

        .radio-label {
            margin-top: -2px;
        }
    }

    &.align-middle {
        align-items: center;
    }

    &.align-bottom {
        align-items: flex-end;
    }
}

.radio-input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.radio-custom {
    position: absolute;
    left: 0;
    height: var(--zs-radio-default-size);
    width: var(--zs-radio-default-size);
    background-color: var(--zs-radio-default-background);
    border: var(--zs-radio-default-border);
    border-radius: var(--zs-radio-default-border-radius);
    box-shadow: none;
    transition: var(--zs-radio-transition);

    &.small {
        height: var(--zs-radio-small-size);
        width: var(--zs-radio-small-size);
        background-color: var(--zs-radio-default-background);
        border: var(--zs-radio-default-border);
    }

    &.medium {
        background-color: var(--zs-radio-default-background);
        border: var(--zs-radio-default-border);
        height: var(--zs-radio-medium-size);
        width: var(--zs-radio-medium-size);
    }

    &.big {
        background-color: white;
        box-shadow: 0 0px 5px rgba(0, 0, 0, 0.1);
        border: 1px solid #eee;
        height: var(--zs-radio-big-size);
        width: var(--zs-radio-big-size);
    }

    /* Color variations */
    &.primary,
    &.blue {
        border-color: var(--color-button-primary);
    }

    &.secondary {
        border-color: var(--color-button-secondary);
    }

    &.success,
    &.green {
        border-color: var(--color-button-success);
    }

    &.danger,
    &.orange {
        border-color: var(--color-button-danger);
    }

    &.warning,
    &.yellow {
        border-color: var(--color-button-warning);
    }

    &.info,
    &.gray,
    &.default {
        border-color: var(--color-button-default);
    }

    &.light-green {
        border-color: var(--color-button-light-success);
    }

    &.white {
        border-color: #FFFFFF;
    }
}

.radio-input:checked~.radio-custom {
    background-color: white;
    border-color: var(--color-blue);

    /* Color variations for checked state */
    &.primary,
    &.blue {
        border-color: var(--color-button-primary);
    }

    &.secondary {
        border-color: var(--color-button-secondary);
    }

    &.success,
    &.green {
        border-color: var(--color-button-success);
    }

    &.danger,
    &.orange {
        border-color: var(--color-button-danger);
    }

    &.warning,
    &.yellow {
        border-color: var(--color-button-warning);
    }

    &.info,
    &.gray,
    &.default {
        border-color: var(--color-button-default);
    }

    &.light-green {
        border-color: var(--color-button-light-success);
    }

    &.white {
        border-color: #FFFFFF;
    }
}

.radio-custom:after {
    content: "";
    position: absolute;
    display: none;
    top: 4px;
    left: 4px;
    width: var(--zs-radio-dot-small-size);
    height: var(--zs-radio-dot-small-size);
    border-radius: 50%;
    background: var(--color-blue);
}

/* Color variations for the inner dot */
.radio-custom.primary:after,
.radio-custom.blue:after {
    background: var(--color-button-primary);
}

.radio-custom.secondary:after {
    background: var(--color-button-secondary);
}

.radio-custom.success:after,
.radio-custom.green:after {
    background: var(--color-button-success);
}

.radio-custom.danger:after,
.radio-custom.orange:after {
    background: var(--color-button-danger);
}

.radio-custom.warning:after,
.radio-custom.yellow:after {
    background: var(--color-button-warning);
}

.radio-custom.info:after,
.radio-custom.gray:after,
.radio-custom.default:after {
    background: var(--color-button-default);
}

.radio-custom.light-green:after {
    background: var(--color-button-light-success);
}

.radio-custom.white:after {
    background: #FFFFFF;
}

.radio-custom.small:after {
    top: var(--zs-radio-dot-small-position);
    left: var(--zs-radio-dot-small-position);
    width: var(--zs-radio-dot-small-size);
    height: var(--zs-radio-dot-small-size);
}

.radio-custom.medium:after {
    top: var(--zs-radio-dot-medium-position);
    left: var(--zs-radio-dot-medium-position);
    width: var(--zs-radio-dot-medium-size);
    height: var(--zs-radio-dot-medium-size);
}

.radio-custom.big:after {
    top: var(--zs-radio-dot-big-position);
    left: var(--zs-radio-dot-big-position);
    width: var(--zs-radio-dot-big-size);
    height: var(--zs-radio-dot-big-size);
}

.radio-input:checked~.radio-custom:after {
    display: block;
}

.radio-label {
    color: var(--zs-radio-label-color);
    font-size: var(--zs-radio-label-font-size);
    margin-left: var(--zs-radio-label-margin-left);
    font-family: var(--zs-radio-label-font-family);
    text-align: justify;
}

/* Focus styles for accessibility */
.radio-input:focus~.radio-custom {
    box-shadow: var(--zs-radio-focus-shadow);
}
</style>